import React, { useEffect, useState } from "react";
import api from "./api";

function urlB64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

const supported =
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

export default function PushToggle() {
  const [state, setState] = useState("loading"); // loading|unsupported|ready|on|denied|error
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!supported) return setState("unsupported");
    if (Notification.permission === "denied") return setState("denied");
    api.push.status().then((d) => setState(d.subscribed ? "on" : "ready")).catch(() => setState("ready"));
  }, []);

  const enable = async () => {
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { setState("denied"); return; }
      const reg = await navigator.serviceWorker.ready;
      const { publicKey } = await api.push.publicKey();
      if (!publicKey) { setState("error"); return; }
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlB64ToUint8Array(publicKey),
        });
      }
      const j = sub.toJSON();
      await api.push.subscribe({ endpoint: j.endpoint, keys: j.keys });
      setState("on");
    } catch (e) {
      setState("error");
    } finally {
      setBusy(false);
    }
  };

  const isIOS = typeof navigator !== "undefined" && /iP(hone|ad|od)/.test(navigator.userAgent);

  return (
    <div className="mt-3 rounded-3xl border border-slate-100 bg-white p-5" data-testid="push-toggle-card">
      <p className="flex items-center gap-2 text-base font-black text-[#0B2D4D]">🔔 Notificaciones</p>
      <p className="mt-1 text-sm text-slate-500">
        Recibe avisos de nuevos mensajes y movimientos de colita en PawCita.
      </p>

      {state === "loading" && <p className="mt-3 text-sm text-slate-400">Cargando…</p>}

      {state === "on" && (
        <p className="mt-3 rounded-full bg-emerald-50 px-4 py-2 text-center text-sm font-bold text-emerald-700" data-testid="push-status-on">
          Notificaciones activadas ✅
        </p>
      )}

      {state === "ready" && (
        <button
          onClick={enable}
          disabled={busy}
          data-testid="push-enable-btn"
          className="mt-3 w-full rounded-full bg-[#FF5C5C] px-4 py-3 text-sm font-bold text-white transition active:translate-y-[1px] disabled:opacity-60"
        >
          {busy ? "Activando…" : "🔔 Activar notificaciones"}
        </button>
      )}

      {state === "denied" && (
        <p className="mt-3 rounded-2xl bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="push-denied">
          Bloqueaste las notificaciones. Habilítalas desde los ajustes de tu navegador/sistema para PawCita.
        </p>
      )}

      {state === "unsupported" && (
        <p className="mt-3 rounded-2xl bg-slate-50 px-4 py-3 text-sm text-slate-600" data-testid="push-unsupported">
          {isIOS
            ? "Para recibir notificaciones en iPhone, agrega PawCita a tu pantalla de inicio y vuelve a intentarlo."
            : "Tu navegador no soporta notificaciones Web Push. La app sigue funcionando normalmente."}
        </p>
      )}

      {state === "error" && (
        <p className="mt-3 rounded-2xl bg-rose-50 px-4 py-3 text-sm text-rose-700" data-testid="push-error">
          No se pudieron activar las notificaciones ahora. Inténtalo más tarde.
        </p>
      )}
    </div>
  );
}
